import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Spin,
  Alert,
  Typography,
} from "antd";
import {
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import companyService from "../../../services/company/company.service";
import { Company, CompanyStatus, PaymentRecord, PaymentStats, TopPayingCompany } from "../../../types/company.types";
import moment from "moment";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import FooterSection from "../../shared/components/footerSection";
import "./styles.css";
import "./dashboard.css";
// Parça bileşenler
import StatCards from "./components/StatCards";
import CompanyStatusChart from "./components/CompanyStatusChart";
import RecentCompanies from "./components/RecentCompanies";
import QuickAccessCards from "./components/QuickAccessCards";
import GrowthChart from "./components/GrowthChart";
import PaymentStatsCards from "./components/PaymentStatsCards";
import RevenueChart from "./components/RevenueChart";
import TopPayingCompanies from "./components/TopPayingCompanies";

const { Title, Text } = Typography;

interface DashboardStats {
  totalCompanies: number;
  activeCompanies: number;
  inactiveCompanies: number;
  suspendedCompanies: number;
  totalEmployees: number;
  totalBranches: number;
  totalDepartments: number;
}

const SuperAdminDashboard: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [recentCompanies, setRecentCompanies] = useState<Company[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalCompanies: 0,
    activeCompanies: 0,
    inactiveCompanies: 0,
    suspendedCompanies: 0,
    totalEmployees: 0,
    totalBranches: 0,
    totalDepartments: 0
  });
  const [paymentStats, setPaymentStats] = useState<PaymentStats | null>(null);
  const [topPayingCompanies, setTopPayingCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [paymentLoading, setPaymentLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('month')
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Şirketleri getir
      const companiesData = await companyService.getAllCompanies();
      setCompanies(companiesData);

      // Son 5 şirketi al
      const sortedCompanies = [...companiesData].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setRecentCompanies(sortedCompanies.slice(0, 5));

      // İstatistikleri hesapla
      calculateStats(companiesData);
      
      // Payment verilerini hesapla
      fetchPaymentData(companiesData);
    } catch (error: any) {
      handleApiError(error, t);
      setError("Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentData = async (companiesData: Company[]) => {
    try {
      setPaymentLoading(true);
      
      const dataToUse = companiesData || companies;

      // Şirket verilerinden payment bilgilerini çıkar
      const paymentStatsData = calculatePaymentStats(dataToUse);
      const topCompaniesData = getTopPayingCompanies(dataToUse, 5);
      
      setPaymentStats(paymentStatsData);
      setTopPayingCompanies(topCompaniesData);
    } catch (error: any) {
      console.error('Ödeme verileri hesaplanırken hata:', error);
      // Hata durumunda boş state'ler bırak
      setPaymentStats(null);
      setTopPayingCompanies([]);
    } finally {
      setPaymentLoading(false);
    }
  };

  const calculateStats = (companies: Company[]) => {
    const newStats = {
      totalCompanies: companies.length,
      activeCompanies: companies.filter(c => c.status === CompanyStatus.ACTIVE).length,
      inactiveCompanies: companies.filter(c => c.status === CompanyStatus.INACTIVE).length,
      suspendedCompanies: companies.filter(c => c.status === CompanyStatus.DELETED).length,
      totalEmployees: 0,
      totalBranches: 0,
      totalDepartments: 0
    };

    // Toplam çalışan, şube ve departman sayılarını hesapla
    companies.forEach(company => {
      newStats.totalEmployees += company.users?.length || 0;
      newStats.totalBranches += company.branches?.length || 0;
      newStats.totalDepartments += company.departments?.length || 0;
    });

    setStats(newStats);
  };

  const calculatePaymentStats = (companies: Company[]): PaymentStats => {
    // Tüm şirketlerden payment history'leri topla
    const allPayments: (PaymentRecord & { companyId: number; companyName: string })[] = [];
    
    companies.forEach(company => {
      if (company?.paymentRecords) {
        company?.paymentRecords.forEach(payment => {
          allPayments.push({
            ...payment,
            companyId: company.id,
            companyName: company.name
          });
        });
      }
    });

    // İstatistikleri hesapla
    const totalRevenue = allPayments.reduce((sum, payment) => 
      payment.paymentStatus === 'paid' ? sum + payment.amountPaid : sum, 0);
    
    const currentMonth = moment().format('YYYY-MM');
    const monthlyRevenue = allPayments
      .filter(payment => payment.paymentStatus === 'paid' && payment.paidAt && moment(payment.paidAt).format('YYYY-MM') === currentMonth)
      .reduce((sum, payment) => sum + payment.amountPaid, 0);

    const totalTransactions = allPayments.length;
    const activeSubscriptions = allPayments.filter(payment => 
      payment.paymentStatus === 'paid' && moment(payment.expiryDate).isAfter(moment())
    ).length;

    const trialUsers = allPayments.filter(p => p.type === 'trial').length;
    const canceledSubscriptions = allPayments.filter(p => 
      moment(p.expiryDate).isBefore(moment()) && p.paymentStatus === 'paid'
    ).length;

    const averageOrderValue = totalTransactions > 0 ? Math.round(totalRevenue / totalTransactions) : 0;
    const averageRevenuePerUser = activeSubscriptions > 0 ? Math.round(totalRevenue / activeSubscriptions) : 0;

    // Son 6 aylık gelir verisi
    const monthlyRevenueData = [];
    for (let i = 5; i >= 0; i--) {
      const month = moment().subtract(i, 'months');
      const monthKey = month.format('YYYY-MM');
      const monthName = month.format('MMM');
      
      const monthPayments = allPayments.filter(payment => 
        payment.paymentStatus === 'paid' && payment.paidAt && moment(payment.paidAt).format('YYYY-MM') === monthKey
      );
      
      monthlyRevenueData.push({
        month: monthName,
        revenue: monthPayments.reduce((sum, payment) => sum + payment.amountPaid, 0),
        transactions: monthPayments.length
      });
    }

    // Durum dağılımı
    const paymentStatusDistribution = {
      paid: allPayments.filter(p => p.paymentStatus === 'paid').length,
      pending: allPayments.filter(p => p.paymentStatus === 'pending').length,
      failed: allPayments.filter(p => p.paymentStatus === 'failed').length,
      refunded: 0, // PaymentRecord'da refunded status yok, varsayılan 0
    };

    // Plan tipi dağılımı
    const planTypeDistribution = {
      premium: allPayments.filter(p => p.type === 'premium' && p.paymentStatus === 'paid').length,
      basic: 0, // PaymentRecord'da basic type yok
      enterprise: 0, // PaymentRecord'da enterprise type yok
    };

    return {
      totalRevenue,
      monthlyRevenue,
      totalTransactions,
      activeSubscriptions,
      trialUsers,
      canceledSubscriptions,
      averageRevenuePerUser,
      averageOrderValue,
      revenueGrowth: 0, // Bu hesaplanabilir
      topPayingCompanies: [], // Ayrı fonksiyonda hesaplanacak
      monthlyRevenueData,
      paymentStatusDistribution,
      planTypeDistribution
    };
  };

  const getTopPayingCompanies = (companies: Company[], limit: number): TopPayingCompany[] => {
    const companyPayments: { [key: string]: { 
      companyName: string, 
      totalPaid: number, 
      lastPayment: string, 
      planType: string, 
      transactionCount: number 
    } } = {};

    companies.forEach(company => {
      if (company?.paymentRecords) {
        const paidPayments = company?.paymentRecords.filter(p => p.paymentStatus === 'paid');
        
        if (paidPayments.length > 0) {
          const totalPaid = paidPayments.reduce((sum, payment) => sum + payment.amountPaid, 0);
          const lastPayment = paidPayments.sort((a, b) => moment(b.paidAt).unix() - moment(a.paidAt).unix())[0];
          
          companyPayments[company.name] = {
            companyName: company.name,
            totalPaid,
            lastPayment: lastPayment.paidAt || lastPayment.startDate,
            planType: lastPayment.type,
            transactionCount: paidPayments.length
          };
        }
      }
    });

    return Object.values(companyPayments)
      .sort((a, b) => b.totalPaid - a.totalPaid)
      .slice(0, limit);
  };

  // Tarih aralığı değişimi için handler
  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value);
    // Gerçek uygulamada burada seçilen aralığa göre veri çekilebilir
  };

  // Şirket verilerinden son 6 aylık büyüme grafiği verisi oluştur
  const getGrowthChartData = () => {
    if (!companies || companies.length === 0) {
      return [];
    }

    // Son 6 ay için boş bir veri yapısı oluştur
    const lastSixMonths: { [key: string]: { name: string, Aktif: number, Pasif: number, Total: number } } = {};
    
    // Son 6 ayı oluştur (şu anki ay dahil)
    for (let i = 5; i >= 0; i--) {
      const date = moment().subtract(i, 'months');
      const monthKey = date.format('YYYY-MM');
      const monthName = date.format('MMM'); // Kısa ay adı
      
      lastSixMonths[monthKey] = {
        name: monthName,
        Aktif: 0,
        Pasif: 0,
        Total: 0
      };
    }
    
    // Her şirket için, oluşturulma ayına göre sayıları arttır
    companies.forEach(company => {
      const createdAt = moment(company.createdAt);
      const monthKey = createdAt.format('YYYY-MM');
      
      // Eğer son 6 ay içindeyse
      if (lastSixMonths[monthKey]) {
        lastSixMonths[monthKey].Total += 1;
        
        if (company.status === CompanyStatus.ACTIVE) {
          lastSixMonths[monthKey].Aktif += 1;
        } else {
          lastSixMonths[monthKey].Pasif += 1;
        }
      }
    });
    
    // Object değerlerini diziye çevir ve döndür
    return Object.values(lastSixMonths);
  };

  // RecentCompanies bileşenine veri göndermek için dönüştürme işlevi
  const mapCompaniesToCompanyData = (companies: Company[]): any[] => {
    return companies.map(company => ({
      id: company.id,
      name: company.name,
      email: company.email || '',  // Undefined olabilecek alanlara varsayılan değer ataması
      phone: company.phone || '',
      taxNumber: company.taxNumber || '',
      status: company.status,
      users: company.users || [],
      branches: company.branches || [],
      departments: company.departments || [],
      createdAt: company.createdAt
    }));
  };

  if (loading) {
    return (
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-center align-items-center">
        <Spin size="large" />
        <p className="mt-3">Veriler yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-center align-items-center">
        <Alert
          message="Hata"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill">
          {/* Üst Başlık ve Filtre Bölümü */}
          <div className="dashboard-header">
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb">
              <div className="my-auto mb-2">
                <Title level={3} className="mb-1 text-white">Süper Admin Paneli</Title>
                <Text type="secondary" className="text-white">
                  Hoş geldiniz! İşte şirketlerinizin genel durumu.
                </Text>
              </div>
            </div>
          </div>

          <div className="dashboard-container">
            {/* Şirket İstatistik Kartları */}
            <StatCards stats={stats} />
            
            {/* Ödeme İstatistik Kartları */}
            {paymentStats ? (
              <PaymentStatsCards stats={paymentStats} loading={paymentLoading} />
            ) : !paymentLoading && (
              <div className="mb-4">
                <Alert
                  message="Ödeme Verileri"
                  description="Ödeme istatistikleri yüklenemedi. Backend bağlantısını kontrol edin."
                  type="warning"
                  showIcon
                  className="border-0"
                />
              </div>
            )}
            
            {/* Grafik Bölümleri */}
            <Row gutter={[16, 16]} className="mb-4">
              <Col xs={24} lg={8}>
                <CompanyStatusChart 
                  data={{
                    activeCompanies: stats.activeCompanies,
                    inactiveCompanies: stats.inactiveCompanies,
                    suspendedCompanies: stats.suspendedCompanies,
                    totalCompanies: stats.totalCompanies
                  }} 
                />
              </Col>
              <Col xs={24} lg={8}>
                <GrowthChart data={getGrowthChartData()} />
              </Col>
              <Col xs={24} lg={8}>
                {topPayingCompanies.length > 0 || paymentLoading ? (
                  <TopPayingCompanies 
                    companies={topPayingCompanies}
                    loading={paymentLoading}
                  />
                ) : (
                  <Card className="border-0 shadow-sm">
                    <div className="text-center p-4">
                      <ExclamationCircleOutlined className="text-warning mb-2" style={{ fontSize: '32px' }} />
                      <Title level={5} className="text-muted">Veri Bulunamadı</Title>
                      <Text type="secondary">En çok ödeme yapan şirket verisi yüklenemedi.</Text>
                    </div>
                  </Card>
                )}
              </Col>
            </Row>

            {/* Gelir Grafiği */}
            {paymentStats ? (
              <Row gutter={16} className="mb-4">
                <Col xs={24}>
                  <RevenueChart 
                    data={paymentStats.monthlyRevenueData}
                    loading={paymentLoading}
                  />
                </Col>
              </Row>
            ) : !paymentLoading && (
              <Row gutter={16} className="mb-4">
                <Col xs={24}>
                  <Card className="border-0 shadow-sm">
                    <div className="text-center p-4">
                      <ExclamationCircleOutlined className="text-warning mb-2" style={{ fontSize: '32px' }} />
                      <Title level={5} className="text-muted">Gelir Grafiği Yüklenemedi</Title>
                      <Text type="secondary">Aylık gelir verisi backend'den alınamadı.</Text>
                    </div>
                  </Card>
                </Col>
              </Row>
            )}
            
            {/* Hızlı Erişim Kartları */}
            <QuickAccessCards companyCount={stats.totalCompanies} />
            
            {/* Son Eklenen Şirketler */}
            <RecentCompanies 
              companies={mapCompaniesToCompanyData(recentCompanies)} 
              loading={false} 
            />
          </div>
        </div>
        
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>
    </>
  );
};

export default SuperAdminDashboard; 